import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { sentryCaptureException } from 'sentry.js';
import * as clientErrTypes from 'config/clientErrorTypes';
import tokenManager from 'client/utils/tokenManager';
import {
    isFetchNetworkError,
    isOneOfErrors,
    isUnauthorizedToGetAccessToken,
    isFeverApiAccessTokenExpired,
    isGetWinnerDuplicateError,
    isGetWinnerEmptySuspendError,
    isGetWinnerWhenKKboxOrderUnfulfillErr,
    isErrorWhenPromoSuspend,
    isFormApiAccessTokenExpired,
    isFormApiDuplicateSubmitFormError,
    isFormApiSetGameResultWhenSuspendErr,
    isSetGameResultWhenContestModuleRecalculatingRank,
    isTooManyAttemptErr,
    isErrorWhenPromoEnd,
    isErrOfGetEntryWhenPromoInvalid,
    isLineLoginCredentialErr,
    isErrPromoSubmitRecaptchaExpired,
    isRequestAtInvalidPromoTime,
    isErrCreateEntriesTimeOut,
    getGeneralErrCode,
    feverErrCodes,
    isErrOfGetEntryWhenReCaptchaMissing
} from 'client/utils/errorObjectChecker';
import { suspendPromo, endPromo } from 'client/redux/modules/promoState';
import {
    showGeneralErrorDialog,
    showPromoNotAvailableErrDialog,
    showDuplicateGetWinnerErrDialog,
    showPromoEndErrDialog,
    showGetPromoDataErrorDialog,
    showUnfufillKKboxOrderDialog,
    showNetworkErrorDialog,
    showUnauthorizedError,
    showContestRecalculatingCacheDialog,
    showTooManyAttemptErrDialog,
    showInvalidTestCodeErrorDialog,
    showRetryRecaptchaDialog,
    showFormSubmitDuplicateErrDialog,
    showGetPromoDataErrorAndNoRefreshBtnDialog,
    showRecaptchaMissingDialog,
    showMergeFrameFailedDialog,
    showWinnerNotFoundDialog,
    showPromoNotFoundDialog,
    showLiffAuthFailDialog,
    showFillFormDuplicateDialog,
    showGetCheckPointCardUnexpectedDialog,
    showOpenTaskUnexpectedDialog,
    showEntryFlowExistsDialog,
    showPointIsLockedErrorDialog,
    showThirdPartyApiTimedOutDialog,
    showTokenErrorDialog,
    showEntryReachTotalLimitDialog
} from 'client/redux/modules/systemErrorDialog';

export default function useClientErrorHandler() {
    const dispatch = useDispatch();

    return useCallback(
        ({ err = {} } = {}) => {
            errorActionHandler(err, { dispatch });
        },
        [dispatch]
    );
}

export function errorActionHandler(errObj, { dispatch }) {
    console.error(errObj);
    const { code: generalErrCode } = getGeneralErrCode(errObj);
    if (generalErrCode) {
        return dispatch(showGetPromoDataErrorDialog(generalErrCode));
    }

    if (errObj.extraInfo) console.error(errObj.extraInfo);

    const { errSerialNo, errType, errMsgFromLoginServer, isGetAccessTokenFailed } =
        errObj.extraInfo ?? {};
    switch (true) {
        case errType === clientErrTypes.GET_API_ERROR_AND_NO_REFRESH_BTN:
            return dispatch(showGetPromoDataErrorAndNoRefreshBtnDialog());
        case errType === clientErrTypes.GET_API_ERROR:
            return dispatch(showGetPromoDataErrorDialog());
        case errType === clientErrTypes.PROMO_NOT_FOUND:
            return dispatch(showPromoNotFoundDialog());
        case errType === clientErrTypes.INVALID_TEST_CODE:
            return dispatch(showInvalidTestCodeErrorDialog());
        case errType === clientErrTypes.LOGIN_GET_ACCESS_TOKEN_FAILED_REWARD_INIT &&
            errMsgFromLoginServer !== undefined:
            return dispatch(
                showGeneralErrorDialog({
                    code: errMsgFromLoginServer
                })
            );
        case isErrCreateEntriesTimeOut(errObj):
            return dispatch(
                showGeneralErrorDialog({
                    code: feverErrCodes.ERR_CREATE_ENTRIES_TIMEOUT
                })
            );
        case isFetchNetworkError(errObj):
            return dispatch(showNetworkErrorDialog());
        case isTooManyAttemptErr(errObj):
            return dispatch(showTooManyAttemptErrDialog());
        case isGetWinnerDuplicateError(errObj):
            return dispatch(
                showDuplicateGetWinnerErrDialog({
                    serial: errSerialNo,
                    code: feverErrCodes.ERR_DUPLICATE_GET_WINNER
                })
            );
        case isRequestAtInvalidPromoTime(errObj):
            return dispatch(showPromoNotAvailableErrDialog());
        case errType === clientErrTypes.PICK_PRIZE_LOTTERY_NO_REMAINDER:
        case isOneOfErrors(
            [
                isGetWinnerEmptySuspendError,
                isErrorWhenPromoSuspend,
                isFormApiSetGameResultWhenSuspendErr,
                isErrOfGetEntryWhenPromoInvalid
            ],
            errObj
        ):
            dispatch(suspendPromo());
            return dispatch(showPromoNotAvailableErrDialog());
        case isErrorWhenPromoEnd(errObj):
            dispatch(endPromo());
            return dispatch(showPromoEndErrDialog());
        case isGetAccessTokenFailed:
        case isOneOfErrors(
            [
                isUnauthorizedToGetAccessToken,
                isFeverApiAccessTokenExpired,
                isFormApiAccessTokenExpired
            ],
            errObj
        ):
            return dispatch(showUnauthorizedError());
        case isFormApiDuplicateSubmitFormError(errObj):
            return dispatch(showFormSubmitDuplicateErrDialog());
        case isGetWinnerWhenKKboxOrderUnfulfillErr(errObj):
            return dispatch(showUnfufillKKboxOrderDialog(errSerialNo));
        case isSetGameResultWhenContestModuleRecalculatingRank(errObj):
            return dispatch(showContestRecalculatingCacheDialog());
        case isErrPromoSubmitRecaptchaExpired(errObj):
            return dispatch(showRetryRecaptchaDialog());
        case isLineLoginCredentialErr(errObj):
            return tokenManager.logout().then(() => window.location.reload());
        case isErrOfGetEntryWhenReCaptchaMissing(errObj):
            return dispatch(showRecaptchaMissingDialog());
        case errType === clientErrTypes.PHOTO_MERGE_FAILED:
            return dispatch(showMergeFrameFailedDialog());
        case errType === clientErrTypes.WINNER_NOT_FOUND:
            return dispatch(showWinnerNotFoundDialog());
        case errType === clientErrTypes.LIFF_INIT_AUTH_FAILED:
            return dispatch(showLiffAuthFailDialog());
        case errType === clientErrTypes.FILL_FORM_DUPLICATE:
            return dispatch(showFillFormDuplicateDialog());
        case errType === clientErrTypes.CHECK_POINT_CARD_FAILED:
            return dispatch(showGetCheckPointCardUnexpectedDialog(errSerialNo));
        case errType === clientErrTypes.TASK_AND_RELATED_PROMO_DISCONNECT:
            return dispatch(showOpenTaskUnexpectedDialog(errSerialNo));
        case errType === clientErrTypes.THIRD_PARTY_API_TIMED_OUT:
            return dispatch(showThirdPartyApiTimedOutDialog());
        case errType === clientErrTypes.TOKEN_ERROR:
            return dispatch(showTokenErrorDialog());
        case errType === clientErrTypes.UGC_GET_MEDIA_COUNTS:
        case errType === clientErrTypes.GET_KKBOX_USER_POINT:
        case errType === clientErrTypes.REDUCE_KKBOX_POINT:
        case errType === clientErrTypes.MGM_LOTTERY_VALIDATION_FAILED.REFERRAL_ENTRY_NOT_FOUND:
        case errType === clientErrTypes.MGM_LOTTERY_VALIDATION_FAILED.EXCEPTION:
        case errType === clientErrTypes.LOGIN_USER_INFO_FAILED:
        case errType === clientErrTypes.PSYCHO_QUIZ_RESULT_PAGE_FAILED:
        case errType === clientErrTypes.PRIZE_OPEN_WINNER:
        case errType === clientErrTypes.NOTIFY_MEMBER_NOT_FOUND:
        case errType === clientErrTypes.NOTIFY_DRIVER_NOT_SUPPORT:
        case errType === clientErrTypes.NOTIFY_CREATE_FAILED:
        case errType === clientErrTypes.NOTIFY_MISSING_REQUIRED_FIELDS:
        case errType === clientErrTypes.MISSION_REWARD_DATE_FORMAT_ERROR:
            sentryCaptureException(errObj);
            break;
        case errType === clientErrTypes.POINTS_IS_NOT_ENOUGH_BY_PRE_CHECK:
        case errType === clientErrTypes.POINTS_IS_NOT_ENOUGH:
            return dispatch(showPromoNotAvailableErrDialog());
        case errType === clientErrTypes.STORE_GAME_RESULT_ENTRY_FLOW_EXISTS:
            return dispatch(showEntryFlowExistsDialog());
        case errType === clientErrTypes.POINT_IS_LOCKED:
            sentryCaptureException(errObj);
            return dispatch(showPointIsLockedErrorDialog());
        case errType === clientErrTypes.ENTRY_REACH_TOTAL_LIMIT:
            return dispatch(showEntryReachTotalLimitDialog());
        case errType === clientErrTypes.NOTIFY_EMAIL_NOT_VALID:
        case errType === clientErrTypes.FORM_DATA_WINNER_NOT_FOUND:
        case errType === clientErrTypes.SURVEY_RESULT_PAGE_FAILED:
            return;
        default: {
            const eventId = sentryCaptureException(errObj);
            dispatch(
                showGeneralErrorDialog({
                    code: eventId,
                    serial: errSerialNo
                })
            );
        }
    }
}

/**
 * 若指定將 body 送往 sentry，需將以下機敏性資料做轉換
 * @property {string} carrierCode - 載具密碼
 * @property {string} carrierNumber - 手機載具
 */
const secretKey = ['carrierCode', 'carrierNumber'];
const convertSecretKey = body => {
    const res = {};
    Object.entries(body).forEach(([key, value]) => {
        res[key] = secretKey.includes(key) ? '******' : value;
    });
    return res;
};

export function generateApiErrObj(apiErr = {}, type, body) {
    let errObj = new Error(type);
    apiErr.errType = type;
    errObj.extraInfo = apiErr;

    if (!Array.isArray(apiErr) && typeof apiErr === 'object' && Boolean(body)) {
        errObj.extraInfo.body = convertSecretKey(body);
    }

    return errObj;
}
